import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNotifications } from '../contexts/NotificationContext'
import socketService from '../services/socket'

interface NewMessageData {
  conversationId: string
  sessionId: string
  message: {
    role: 'user'
    content: string
    timestamp: string
  }
  response: {
    role: 'assistant'
    content: string
    timestamp: string
  }
  conversationInfo: {
    isActive: boolean
    messageCount: number
    lastMessageAt: string
  }
}

interface NewConversationData {
  conversationId: string
  sessionId: string
  startedAt: string
  isActive: boolean
  source: string
  deviceType: string
  ipAddress: string
}

export const useRealTimeUpdates = () => {
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const [isConnected, setIsConnected] = useState(false)

  /**
   * Connection state has to live in React state.
   *
   * This previously returned `socketService.isConnected()` read during render.
   * The socket connects asynchronously, so nothing re-rendered when it did and
   * the header sat on "Not live" forever while messages were arriving fine.
   *
   * Kept in its own effect with no dependencies: the handler effect below
   * re-runs whenever `addNotification` changes identity, and re-subscribing
   * connection listeners on every one of those would be pointless churn.
   */
  useEffect(() => {
    const socket = socketService.connect()

    const handleConnect = () => setIsConnected(true)
    const handleDisconnect = () => setIsConnected(false)

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)

    // The socket may already have connected before these listeners attached,
    // in which case the 'connect' event has been and gone.
    setIsConnected(socket.connected)

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
    }
  }, [])

  useEffect(() => {
    // Connect to socket
    socketService.connect()

    const handleNewMessage = (data: NewMessageData) => {
      console.log('New message received:', data)
      
      // Add notification
      addNotification({
        type: 'new_message',
        title: 'New Message',
        message: `New message in conversation ${data.sessionId.substring(0, 8)}...`,
        conversationId: data.conversationId,
      })

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation', data.conversationId] })
    }

    const handleNewConversation = (data: NewConversationData) => {
      console.log('New conversation started:', data)
      
      // Add notification
      addNotification({
        type: 'new_conversation',
        title: 'New Conversation',
        message: `New conversation started from ${data.source}`,
        conversationId: data.conversationId,
      })

      // Invalidate conversations list to show the new conversation
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    }

    const handleConversationEnd = (data: any) => {
      console.log('Conversation ended:', data)
      
      // Add notification
      addNotification({
        type: 'conversation_ended',
        title: 'Conversation Ended',
        message: `Conversation ${data.sessionId.substring(0, 8)}... has ended`,
        conversationId: data.conversationId,
      })

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation', data.conversationId] })
    }

    // Set up event listeners
    socketService.onNewMessage(handleNewMessage)
    socketService.onNewConversation(handleNewConversation)
    socketService.onConversationEnd(handleConversationEnd)

    // Cleanup function
    return () => {
      socketService.offNewMessage(handleNewMessage)
      socketService.offNewConversation(handleNewConversation)
      socketService.offConversationEnd(handleConversationEnd)
      socketService.disconnect()
    }
  }, [queryClient, addNotification])

  return { isConnected }
}